import { zodResolver } from '@hookform/resolvers/zod';
import { Combobox } from '@optools/components/combobox/combobox';
import { Spinner } from '@optools/components/loading/spinner';
import { Button } from '@optools/components/ui/button';
import { DialogFooter } from '@optools/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import { ProgramSource } from '@optools/services/operator-gateway/types/generated/graphql';
import { useCallSignsQuery } from '@optools/store/statsApiSlice';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ProgramSourceSelect } from './program-source-select';
const formSchema = z.object({
  callSign: z.string().min(1, { error: 'Call sign is required' }),
  gameSource: z.enum(ProgramSource).nullable().optional(),
  postgameSource: z.enum(ProgramSource).nullable().optional(),
  pregameSource: z.enum(ProgramSource).nullable().optional(),
});

export type BroadcastSourceOverrideFormValues = z.infer<typeof formSchema>;

export interface BroadcastSourceOverrideFormProps {
  defaultValues?: BroadcastSourceOverrideFormValues;
  disabled?: boolean;
  onCancel: () => void;
  onSubmit: (values: BroadcastSourceOverrideFormValues) => void;
}

export function BroadcastSourceOverrideForm({
  defaultValues,
  disabled = false,
  onCancel,
  onSubmit,
}: BroadcastSourceOverrideFormProps) {
  const {
    data: callSignData,
    error: callSignError,
    isLoading: callSignIsLoading,
  } = useCallSignsQuery();

  const callSignValues = useMemo(() => {
    if (!callSignData) {
      return [];
    }

    const formattedCallSigns = [
      ...new Set(callSignData.filter((item) => item).map((item) => item.trim())),
    ];
    return formattedCallSigns.sort().map((item) => {
      return {
        label: item,
        value: item,
      };
    });
  }, [callSignData]);

  const form = useForm<BroadcastSourceOverrideFormValues>({
    defaultValues: {
      callSign: '',
      gameSource: ProgramSource.UNKNOWN,
      postgameSource: ProgramSource.UNKNOWN,
      pregameSource: ProgramSource.UNKNOWN,
      ...defaultValues,
    },
    disabled,
    resolver: zodResolver(formSchema),
  });

  return (
    <Form {...form}>
      <form
        className="mt-2 flex flex-col gap-6"
        onReset={() => {
          form.reset();
          onCancel();
        }}
        onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="callSign"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Call Sign</FormLabel>
                <FormControl className="w-44">
                  {callSignIsLoading ? (
                    <Spinner />
                  ) : callSignError ? (
                    <Input {...field} />
                  ) : (
                    <Combobox
                      data={callSignValues}
                      useIndexInKey={true}
                      value={field.value}
                      onValueChange={field.onChange}
                    />
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <div className="flex flex-wrap items-center justify-start gap-2">
          <FormField
            control={form.control}
            name="pregameSource"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Pre-Game Source</FormLabel>
                  <FormControl>
                    <ProgramSourceSelect
                      value={field.value ?? ProgramSource.UNKNOWN}
                      onValueChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="gameSource"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Game Source</FormLabel>
                  <FormControl>
                    <ProgramSourceSelect
                      value={field.value ?? ProgramSource.UNKNOWN}
                      onValueChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="postgameSource"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Post-Game Source</FormLabel>
                  <FormControl>
                    <ProgramSourceSelect
                      value={field.value ?? ProgramSource.UNKNOWN}
                      onValueChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        <DialogFooter>
          <Button disabled={form.formState.disabled} type="reset" variant="secondary">
            Cancel
          </Button>
          <Button
            aria-label="submit"
            disabled={form.formState.disabled}
            loading={form.formState.disabled}
            type="submit">
            Submit
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
